import { useEffect, useMemo } from 'react';

import { useAppDispatch, useAppSelector } from '@verantos-dev/core-annotation-tool';
import { useFeatureFlagSplit } from '@verantos-dev/core-utils';

import { useGetDataSplitSummaryQuery } from '../../api/createProjectApiSlice';
import { CP_DISABLE_ASYM_ALLOC_FOR_2_ANNOTATORS_FLAG } from '../../constants';
import {
  type AgentAllocationPreviewStatus,
  useAnnotationAgentAllocationPreview,
  useIsAnnotationAgentOn,
} from '../../hooks/useAnnotationAgentAllocation';
import {
  setDefaultAllocationMode,
  clearDefaultAllocationMode,
} from '../../state/createProjectSlice';
import {
  selectDefaultAllocationMode,
  selectHumanSharePercentages,
  selectSelectedAnnotators,
  selectSelectedSupervisors,
  selectWorkflowId,
} from '../../state/selector';
import type { AnnotationAgentAllocationPreviewRequest } from '../../types/request';
import type { AnnotationAgentAllocationPreviewResponse } from '../../types/response';
import { summarizeCustomShares } from '../../utils/agentAllocation';

interface UsePeopleAllocationProps {
  setRequiredFieldsFilled: (fieldsFilled: boolean) => void;
}

// TODO: cleanup with cp_annotation_createproject_agent ff
interface AgentAllocation {
  status: AgentAllocationPreviewStatus;
  preview: AnnotationAgentAllocationPreviewResponse | null;
  /** n once the preview is ready, else 0. */
  encounterCount: number;
}

interface UsePeopleAllocationResults {
  defaultAllocationMode: boolean;
  isCustomAllocationDisabled: boolean;
  setAllocationModeDefault: () => void;
  setAllocationModeCustom: () => void;
  // TODO: cleanup with cp_annotation_createproject_agent ff
  /** The n preview while the annotation agent is on; null while it is off. */
  agentAllocation: AgentAllocation | null;
}

// TODO: cleanup with cp_annotation_createproject_agent ff
// Stable empty list: step 6 asks the allocation preview for n only.
const NO_HUMANS: AnnotationAgentAllocationPreviewRequest['humans'] = [];

export const usePeopleAllocation = ({
  setRequiredFieldsFilled,
}: UsePeopleAllocationProps): UsePeopleAllocationResults => {
  const dispatch = useAppDispatch();
  const defaultAllocationMode = useAppSelector(selectDefaultAllocationMode);
  const selectedAnnotators = useAppSelector(selectSelectedAnnotators);
  const selectedSupervisors = useAppSelector(selectSelectedSupervisors);
  // TODO: cleanup with cp_annotation_createproject_agent ff
  const humanSharePercentages = useAppSelector(selectHumanSharePercentages);

  // TODO: cleanup with cp_annotation_createproject_agent ff
  // Step 6 only needs n, so the humans are not sent and choosing people does not refetch it.
  const isAgentOn = useIsAnnotationAgentOn();
  // In Custom too: step 6 computes the human counts from n as the shares are typed.
  const { status: agentPreviewStatus, preview: agentPreview } = useAnnotationAgentAllocationPreview(
    isAgentOn,
    'default',
    NO_HUMANS
  );
  const agentEncounterCount =
    agentPreviewStatus === 'ready' ? agentPreview?.encounterCount ?? 0 : 0;

  // TODO: cleanup with cp_disable_asym_alloc_for2_annotators ff
  const disableAsymAllocFor2Annotators = useFeatureFlagSplit(
    CP_DISABLE_ASYM_ALLOC_FOR_2_ANNOTATORS_FLAG
  ) as boolean;
  // Every encounter needs 2 annotators, so an asymmetric split is impossible
  // with fewer than 3 annotators.
  const isAsymAllocationDisabled = disableAsymAllocFor2Annotators && selectedAnnotators.length < 3;
  // TODO: cleanup with cp_annotation_createproject_agent ff
  // With the agent on, each encounter needs one human, so the annotator-count rule does not apply.
  const isCustomAllocationDisabled = !isAgentOn && isAsymAllocationDisabled;

  const setAllocationModeDefault = () => {
    dispatch(setDefaultAllocationMode());
  };
  const setAllocationModeCustom = () => {
    if (isCustomAllocationDisabled) {
      return;
    }
    dispatch(clearDefaultAllocationMode());
  };

  // Revert to default allocation if custom mode was selected and custom allocation then became
  // unavailable because the annotator count dropped below the minimum.
  useEffect(() => {
    if (isCustomAllocationDisabled && !defaultAllocationMode) {
      dispatch(setDefaultAllocationMode());
    }
  }, [isCustomAllocationDisabled, defaultAllocationMode, dispatch]);

  const workflowId = useAppSelector(selectWorkflowId);
  const { data: dataSplitSummaryData } = useGetDataSplitSummaryQuery({
    workflowId: workflowId ?? '',
  });
  const dataSplitSummary = useMemo(
    () => dataSplitSummaryData?.data ?? { trainingCount: 0, testCount: 0, validationCount: 0 },
    [dataSplitSummaryData]
  );

  useEffect(() => {
    // TODO: cleanup with cp_annotation_createproject_agent ff
    if (isAgentOn) {
      // In Custom, every human needs a whole-number share and the shares must total 100.
      const areSharesValid =
        defaultAllocationMode ||
        summarizeCustomShares(
          agentEncounterCount,
          selectedAnnotators.map(({ id }) => humanSharePercentages[id] ?? '')
        ).isValid;
      setRequiredFieldsFilled(
        selectedAnnotators.length > 0 &&
          selectedSupervisors.length > 0 &&
          agentEncounterCount > 0 &&
          areSharesValid
      );
      return;
    }
    const trainingTotal = selectedAnnotators.reduce(
      (acc, annotator) => acc + (annotator.allocationPercentage?.trainingPercentage ?? 0),
      0
    );
    const testTotal = selectedAnnotators.reduce(
      (acc, annotator) => acc + (annotator.allocationPercentage?.testPercentage ?? 0),
      0
    );
    const validationTotal = selectedAnnotators.reduce(
      (acc, annotator) => acc + (annotator.allocationPercentage?.validationPercentage ?? 0),
      0
    );
    const allFieldsFilled = Boolean(
      selectedAnnotators.length > 0 &&
        selectedSupervisors.length > 0 &&
        (dataSplitSummary.trainingCount === 0 || trainingTotal === 100 || defaultAllocationMode) &&
        (dataSplitSummary.testCount === 0 || testTotal === 100 || defaultAllocationMode) &&
        (dataSplitSummary.validationCount === 0 || validationTotal === 100 || defaultAllocationMode)
    );
    setRequiredFieldsFilled(allFieldsFilled);
  }, [
    selectedAnnotators,
    selectedSupervisors,
    defaultAllocationMode,
    dataSplitSummary,
    isAgentOn,
    agentEncounterCount,
    humanSharePercentages,
  ]);

  return {
    defaultAllocationMode,
    isCustomAllocationDisabled,
    setAllocationModeDefault,
    setAllocationModeCustom,
    agentAllocation: isAgentOn
      ? { status: agentPreviewStatus, preview: agentPreview, encounterCount: agentEncounterCount }
      : null,
  };
};
